import React, { useState, useEffect } from 'react';
import { Box, Paper, Tabs, Tab } from '@mui/material';
import ScientificCalculatorUI from './ScientificCalculatorUI';
import GraphingCalculator from './GraphingCalculator';
import CalculatorHistory from './CalculatorHistory';
import AccessibilitySettings from './AccessibilitySettings';
import { getAnalytics, logEvent } from 'firebase/analytics';

const ScientificCalculator = () => {
  // State for calculator
  const [tabValue, setTabValue] = useState(0);
  const [history, setHistory] = useState([]);
  const [highContrastMode, setHighContrastMode] = useState(false);
  const [largeButtonsMode, setLargeButtonsMode] = useState(false);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Log tab change to Firebase Analytics
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'calculator_tab_change', {
        calculator_type: 'scientific',
        tab_index: newValue,
      });
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };

  // Handle adding to history
  const addToHistory = (historyItem) => {
    setHistory([...history, historyItem]);
  };

  // Handle clearing history
  const clearHistory = () => {
    setHistory([]);
  };

  // Handle using a history item
  const useHistoryItem = (item) => {
    // This will be implemented to pass the value back to the calculator
    console.log('Using history item:', item);
  };

  // Load accessibility settings from localStorage on mount
  useEffect(() => {
    const savedHighContrast = localStorage.getItem('highContrastMode');
    const savedLargeButtons = localStorage.getItem('largeButtonsMode');
    
    if (savedHighContrast) {
      setHighContrastMode(savedHighContrast === 'true');
    }
    
    if (savedLargeButtons) {
      setLargeButtonsMode(savedLargeButtons === 'true');
    }
  }, []);

  // Save accessibility settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('highContrastMode', highContrastMode);
    localStorage.setItem('largeButtonsMode', largeButtonsMode);
    
    // Apply high contrast class to body if needed
    if (highContrastMode) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    
    // Apply large buttons class to body if needed
    if (largeButtonsMode) {
      document.body.classList.add('large-buttons');
    } else {
      document.body.classList.remove('large-buttons');
    }
  }, [highContrastMode, largeButtonsMode]);

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 2, 
          bgcolor: highContrastMode ? 'black' : 'background.paper',
          color: highContrastMode ? 'white' : 'text.primary',
        }}
      >
        {tabValue === 0 && (
          <ScientificCalculatorUI 
            addToHistory={addToHistory}
            highContrastMode={highContrastMode}
            largeButtonsMode={largeButtonsMode}
          />
        )}
        
        {tabValue === 1 && (
          <GraphingCalculator 
            highContrastMode={highContrastMode}
            largeButtonsMode={largeButtonsMode}
          />
        )}
        
        {tabValue === 2 && (
          <CalculatorHistory 
            history={history}
            onClearHistory={clearHistory}
            onUseHistoryItem={useHistoryItem}
            highContrastMode={highContrastMode}
            largeButtonsMode={largeButtonsMode}
          />
        )}
        
        {tabValue === 3 && (
          <AccessibilitySettings 
            highContrastMode={highContrastMode}
            largeButtonsMode={largeButtonsMode}
            onHighContrastChange={setHighContrastMode}
            onLargeButtonsChange={setLargeButtonsMode}
          />
        )}
      </Paper>
    </Box>
  );
};

export default ScientificCalculator;
